import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import Tag from '@/components/base/Tag';
import EmptyState from '@/components/base/EmptyState';
import { newsList } from '@/mocks/newsList';
import { useFavorites } from '@/hooks/useFavorites';
import { useCopy } from '@/hooks/useCopy';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const item = newsList.find((news) => news.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const { copied, copy } = useCopy();

  usePageMeta(item ? `${item.title} - 漂亮的姐姐` : '资讯详情 - 漂亮的姐姐', item?.summary);

  if (!item) {
    return (
      <div className="w-full max-w-page mx-auto px-4 md:px-6 py-10">
        <EmptyState
          title="未找到该资讯"
          description="这篇资讯可能已被移除或链接有误，去看看其他热门内容吧。"
          actionLabel="返回资讯列表"
          icon="ri-emotion-sad-line"
          onAction={() => {
            window.REACT_APP_NAVIGATE('/category/news');
          }}
        />
      </div>
    );
  }

  const paragraphs = item.content.split('\n\n').filter(Boolean);
  const favorite = isFavorite(item.id);
  const related = newsList.filter((news) => news.id !== item.id && news.tags.some((t) => item.tags.includes(t))).slice(0, 3);

  return (
    <div className="w-full max-w-page mx-auto px-4 md:px-6 py-6 pb-24 lg:pb-6">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '影视资讯', to: '/category/news' }, { label: item.tags[0] || '资讯' }]} />

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <article className="lg:col-span-8">
          <div className="flex flex-wrap gap-2">
            {item.tags.map((tag) => (
              <Tag key={tag} label={tag} variant="accent" />
            ))}
          </div>
          <h1 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">{item.title}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-calendar-line"></i>
              {item.date}
            </span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-user-line"></i>
              {item.author}
            </span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-newspaper-line"></i>
              漂亮的姐姐 影视资讯
            </span>
          </div>

          <div className="mt-5 rounded-lg overflow-hidden aspect-[16/9] bg-background-200">
            <img src={item.cover} alt={item.title} title={`${item.title} 资讯配图`} className="w-full h-full object-cover object-top" />
          </div>

          <div className="mt-6 flex flex-col gap-4">
            {paragraphs.map((paragraph, index) => (
              <p key={index} className="text-sm md:text-base text-foreground-800 leading-loose">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3 pt-6 border-t border-background-200">
            <button
              type="button"
              onClick={() => toggleFavorite(item.id)}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-label font-medium transition-colors cursor-pointer whitespace-nowrap ${
                favorite
                  ? 'bg-accent-500 text-background-50 dark:text-foreground-950 hover:bg-accent-600'
                  : 'bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600'
              }`}
            >
              <i className={favorite ? 'ri-bookmark-fill' : 'ri-bookmark-line'}></i>
              {favorite ? '已收藏' : '收藏这篇资讯'}
            </button>
            <button
              type="button"
              onClick={() => copy(window.location.href)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-foreground-800 text-sm font-label hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className={copied ? 'ri-check-line text-accent-600' : 'ri-link'}></i>
              {copied ? '链接已复制' : '复制分享链接'}
            </button>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-foreground-500 font-label">所属分类：</span>
            <Link to="/category/news" className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap">
              影视资讯
            </Link>
            {item.tags.map((tag) => (
              <Link key={tag} to="/category/news" className="px-3 py-1 rounded-full bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                #{tag}
              </Link>
            ))}
          </div>
        </article>

        <aside className="lg:col-span-4">
          <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden lg:sticky lg:top-20">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-background-200 bg-primary-50">
              <span className="w-8 h-8 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
                <i className="ri-links-line"></i>
              </span>
              <h2 className="font-heading text-base font-bold text-foreground-950">相关推荐资讯</h2>
            </div>
            <div className="divide-y divide-background-200">
              {(related.length > 0 ? related : newsList.slice(0, 3)).map((news) => (
                <Link key={news.id} to={`/detail/news/${news.id}`} className="block p-4 hover:bg-background-100 transition-colors cursor-pointer">
                  <h3 className="font-heading text-sm font-bold text-foreground-900 line-clamp-2 hover:text-primary-600 transition-colors">{news.title}</h3>
                  <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-calendar-line"></i>
                      {news.date}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 font-label whitespace-nowrap">{news.tags[0]}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <Link to="/category/news" className="inline-flex items-center gap-1 text-sm font-label text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-arrow-left-line"></i>
          返回资讯列表
        </Link>
        <Link to="/" className="inline-flex items-center gap-1 text-sm font-label text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
          回到首页
        </Link>
      </div>

      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-background-50/95 backdrop-blur border-t border-background-200 px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => toggleFavorite(item.id)}
          className={`flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-md text-sm font-label font-medium transition-colors cursor-pointer whitespace-nowrap ${
            favorite ? 'bg-accent-500 text-background-50 dark:text-foreground-950' : 'bg-primary-500 text-background-50 dark:text-foreground-950'
          }`}
        >
          <i className={favorite ? 'ri-bookmark-fill' : 'ri-bookmark-line'}></i>
          {favorite ? '已收藏' : '收藏'}
        </button>
        <button
          type="button"
          onClick={() => copy(window.location.href)}
          className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-md border border-background-300 text-foreground-800 text-sm font-label cursor-pointer whitespace-nowrap"
        >
          <i className={copied ? 'ri-check-line text-accent-600' : 'ri-link'}></i>
          {copied ? '已复制' : '分享'}
        </button>
      </div>
    </div>
  );
}